import React, { useState, useRef, useEffect } from "react";
import { X, Plane } from "lucide-react";
import { searchAirports, resolveAirport } from "../data/mock";
import AirportDropdown from "./AirportDropdown";

function SelectedAirport({ code, height, label, testId, onClear }) {
  const a = resolveAirport(code);
  return (
    <div data-testid={testId ? `${testId}-selected` : undefined} className={`flex ${height} items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3`}>
      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-900">
        <Plane className="h-3.5 w-3.5 shrink-0 text-blue-600" />
        {code}
        {a && !a.synthetic && <span className="truncate text-xs font-normal text-slate-500">{a.city}</span>}
      </span>
      <button onClick={onClear} aria-label={`Clear ${label || "airport"}`} data-testid={testId ? `${testId}-clear` : undefined} className="-mr-2 flex h-10 w-10 items-center justify-center text-slate-400 hover:text-slate-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function useAirportQuery() {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const matches = searchAirports(q, 8);
  const codeGuess = q.trim().toUpperCase();
  const customCode = /^[A-Z]{3}$/.test(codeGuess) && !matches.some((a) => a.code === codeGuess) ? codeGuess : null;
  const reset = () => { setQ(""); setHi(0); };
  return { q, setQ, hi, setHi, matches, customCode, reset };
}

// Autocomplete for any airport worldwide — by IATA code, city or airport name.
export default function AirportPicker({ label, value, onChange, testId, className = "", size = "md" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const height = size === "sm" ? "h-10" : "h-11";
  const { q, setQ, hi, setHi, matches, customCode, reset } = useAirportQuery();

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (code) => { onChange(code); reset(); setOpen(false); };

  const clear = () => {
    onChange("");
    setTimeout(() => ref.current?.querySelector("input")?.focus(), 0);
  };

  const onKeyDown = (e) => {
    const handlers = {
      ArrowDown: () => setHi((i) => Math.min(i + 1, matches.length - 1)),
      ArrowUp: () => setHi((i) => Math.max(i - 1, 0)),
      Enter: () => (matches[hi] ? pick(matches[hi].code) : customCode && pick(customCode)),
      Escape: () => setOpen(false),
    };
    if (handlers[e.key]) { e.preventDefault(); handlers[e.key](); }
  };

  const showDropdown = open && !value && (matches.length > 0 || customCode);

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>}
      {value ? (
        <SelectedAirport code={value} height={height} label={label} testId={testId} onClear={clear} />
      ) : (
        <input
          value={q}
          data-testid={testId}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="City, airport or code"
          className={`${height} w-full rounded-lg border border-slate-200 bg-white px-3 text-base outline-none sm:text-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100`}
        />
      )}
      {showDropdown && <AirportDropdown matches={matches} highlighted={hi} customCode={customCode} onPick={pick} onHover={setHi} testId={testId} />}
    </div>
  );
}
